import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Divider } from "@material-ui/core";
import { savePaymentMethod } from "../redux/healthcareCartSlice";
import { PAYMENT_METHODS } from "../config/market";
import styled from "styled-components";
import HealthcareNavBar from "../components/HealthcareNavBar";
import CheckoutStepper from "../components/CheckoutStepper";
import AgeVerificationModal from "../components/AgeVerificationModal/AgeVerificationModal";
import { resolveProductPaymentPolicy } from "../utils/productPaymentPolicy";
import useCodPolicy from "../hooks/useCodPolicy";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding-top: 80px;
  padding-bottom: 20px;
  background-color: #f5f5f5;

  @media (max-width: 850px) {
    padding-top: var(--lusuku-nav-h, 140px);
    justify-content: flex-start;
  }
`;

const PaymentOptionButton = styled.button`
  border: none;
  padding: 15px 20px;
  background-color: #E91E63;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  &:hover {
    background-color: #C2185B;
  }
  &:disabled {
    background-color: #ccc;
    color: #aaa;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

const HealthcarePaymentScreen = () => {
    const { vendorId } = useParams();
    const dispatch = useDispatch();
    const history = useHistory();

    const cart = useSelector((state) => state.healthcareCart.carts[vendorId] || {});
    const vendor = useSelector((state) => state.healthcareCart.vendor);
    const products = Object.values(cart);

    const [selectedOption, setSelectedOption] = useState(-1);
    const [showAgeVerification, setShowAgeVerification] = useState(false);

    // Check for restricted items in cart
    const restrictedItems = products.filter(
        (item) => item.requiresPrescription || item.ageRestriction > 0 || item.controlledSubstance
    );

    const hasRestrictedItems = restrictedItems.length > 0;

    useEffect(() => {
        if (products.length === 0) {
            history.push(`/healthcare/shop/${vendorId}`);
        }
    }, [history, products, vendorId]);

    const submitHandler = (e) => {
        e.preventDefault();

        // Check if age verification is needed and not yet done
        if (hasRestrictedItems) {
            const ageVerified = sessionStorage.getItem('ageVerified');
            const verifiedTime = sessionStorage.getItem('ageVerifiedTime');
            const oneHour = 60 * 60 * 1000;

            // Check if verification is recent (within last hour)
            const isRecentlyVerified = ageVerified && verifiedTime &&
                (Date.now() - parseInt(verifiedTime)) < oneHour;

            if (!isRecentlyVerified) {
                setShowAgeVerification(true);
                return;
            }
        }

        proceedToPlaceOrder();
    };

    const proceedToPlaceOrder = () => {
        // Method keys from the ACTIVE market registry (config/market.js).
        dispatch(savePaymentMethod(paymentOptions[selectedOption].key));
        history.push(`/healthcare/placeorder/${vendorId}`);
    };

    const handleAgeVerificationConfirm = () => {
        setShowAgeVerification(false);
        proceedToPlaceOrder();
    };

    const handleOptionChange = (index) => {
        setSelectedOption(index);
    };

    /* The live platform COD ceiling. Until it has loaded this is null and
       the policy below leaves Cash on delivery exactly where it was — see
       resolveProductPaymentPolicy for why a shipped constant would be worse
       than no ceiling at all. */
    const { maxProductAmount: codMaxProductAmount } = useCodPolicy();
    const paymentPolicy = resolveProductPaymentPolicy(products, PAYMENT_METHODS, {
      codMaxProductAmount,
    });
    const paymentOptions = paymentPolicy.options;

    useEffect(() => {
        if (selectedOption >= paymentOptions.length) setSelectedOption(-1);
        if (selectedOption === -1 && paymentOptions.length === 1) setSelectedOption(0);
    }, [paymentOptions.length, selectedOption]);

    const optionStyle = (isSelected) => ({
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "12px 16px",
        borderRadius: "4px",
        cursor: "pointer",
        backgroundColor: isSelected ? "#fce4ec" : "#EDF2F7",
        boxShadow: isSelected ? "0px 2px 5px rgba(233, 30, 99, 0.3)" : "none",
        transition: "all 0.3s",
        textAlign: "center",
        fontWeight: isSelected ? "bold" : "normal",
        marginBottom: "16px",
        height: "60px",
    });

    const cartCount = Object.values(cart).reduce((total, item) => total + item.quantity, 0);

    return (
        <>
            <HealthcareNavBar
                vendor={vendor}
                cartCount={cartCount}
            />
            <PageWrapper>
                <div
                    style={{
                        width: "100%",
                        maxWidth: "600px",
                        padding: "24px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                >
                    <CheckoutStepper currentStep="payment" />
                    <h1 style={{ textAlign: "center", marginBottom: "24px", color: "#E91E63" }}>
                        Healthcare Payment Method
                    </h1>

                    {hasRestrictedItems && (
                        <div style={{
                            backgroundColor: "#fff3e0",
                            border: "2px solid #f57c00",
                            borderRadius: "8px",
                            padding: "12px",
                            marginBottom: "20px",
                            textAlign: "center"
                        }}>
                            <p style={{ margin: 0, color: "#f57c00", fontWeight: 600 }}>
                                ⚠️ Age verification required for this order
                            </p>
                        </div>
                    )}

                    <Divider style={{ marginBottom: "24px" }} />

                    <form onSubmit={submitHandler}>
                        {paymentPolicy.notice && (
                            <div style={{ background: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412", borderRadius: 6, padding: "8px 12px", marginBottom: 12 }}>
                                ⚠️ {paymentPolicy.notice}
                            </div>
                        )}
                        {paymentOptions.map((option, index) => (
                            <div
                                key={index}
                                style={optionStyle(selectedOption === index)}
                                onClick={() => handleOptionChange(index)}
                            >
                                <input
                                    type="radio"
                                    checked={selectedOption === index}
                                    onChange={() => handleOptionChange(index)}
                                    style={{ cursor: "pointer", width: "20px", height: "20px" }}
                                />
                                {option.imageUrl ? (
                                    <img
                                        src={option.imageUrl}
                                        alt={option.label}
                                        style={{ width: "50px", height: "50px", objectFit: "contain" }}
                                    />
                                ) : (
                                    <div style={{ width: "50px", height: "50px" }} />
                                )}
                                <span style={{ flex: 1, textAlign: "left" }}>{option.label}</span>
                            </div>
                        ))}

                        <Divider style={{ margin: "24px 0" }} />

                        <PaymentOptionButton type="submit" disabled={selectedOption === -1}>
                            {hasRestrictedItems ? "Continue (Verification Required)" : "Continue to Order Review"}
                        </PaymentOptionButton>
                    </form>
                </div>
            </PageWrapper>

            {showAgeVerification && (
                <AgeVerificationModal
                    open={showAgeVerification}
                    onClose={() => setShowAgeVerification(false)}
                    onConfirm={handleAgeVerificationConfirm}
                    restrictedItems={restrictedItems}
                />
            )}
        </>
    );
};

export default HealthcarePaymentScreen;

